import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { ConversationPlatform } from "../../conversation";

const percentChangeSchema = z.preprocess(
  (val) => {
    if (val === null || val === undefined) return 0;
    if (val === Infinity) return "Infinity";
    if (val === -Infinity) return "-Infinity";
    return val;
  },
  z.union([z.number(), z.literal("Infinity"), z.literal("-Infinity")]),
);

// Internal parameter schemas for analytics functions
export const supportAnalyticsParamsSchema = z.object({
  teamId: z.string(),
  start: z.string(),
  end: z.string(),
  channels: z.array(z.string()).optional(),
  autopilotUserId: z.string(),
});

export const analyticsParamsSchema = z.object({
  start: z.string(),
  end: z.string(),
  platforms: z.array(createEnum(ConversationPlatform)).optional(),
  team: z.any(), // Team model - using z.any() for now as it's a Mongoose model
  prevPeriodStart: z.string(),
  prevPeriodEnd: z.string(),
});

// Raw database response schemas
export const supportAnalyticsRawDataSchema = z.object({
  TOTAL_TICKETS: z.preprocess((val) => val ?? 0, z.number()),
  CREATED_TICKETS: z.preprocess((val) => val ?? 0, z.number()),
  RESOLVED_TICKETS: z.preprocess((val) => val ?? 0, z.number()),
  CLOSED_TICKETS: z.preprocess((val) => val ?? 0, z.number()),
  ONE_TOUCH_RESOLVED_TICKETS: z.preprocess((val) => val ?? 0, z.number()),
  TWO_TOUCH_RESOLVED_TICKETS: z.preprocess((val) => val ?? 0, z.number()),
  THREE_TOUCH_RESOLVED_TICKETS: z.preprocess((val) => val ?? 0, z.number()),
  MULTI_TOUCH_RESOLVED_TICKETS: z.preprocess((val) => val ?? 0, z.number()),
  AVG_CONVERSATION_TOUCH_COUNT: z.preprocess((val) => val ?? 0, z.number()),
  FIRST_MERCHANT_RESPONSE_LAG: z.preprocess(
    (val) => val ?? [],
    z.array(z.number()),
  ),
  RESOLUTION_LAG: z.preprocess((val) => val ?? [], z.array(z.number())),
  AVG_RESPONSE_TIME: z.preprocess((val) => val ?? 0, z.number()),
  AVG_CSAT_RATING: z.preprocess((val) => val ?? 0, z.number()),
  CSAT_SURVEYS_SENT: z.preprocess((val) => val ?? 0, z.number()),
  CSAT_RESPONSE_COUNT: z.preprocess((val) => val ?? 0, z.number()),
});

export const aiUserAnalyticsSchema = z.object({
  conversationsResolved: z.preprocess((val) => val ?? 0, z.number()),
  resolutionTimes: z.preprocess((val) => val ?? [], z.array(z.number())),
  csatRating: z.preprocess((val) => val ?? 0, z.number()),
});

export const humanUserAnalyticsSchema = z.object({
  conversationsResolved: z.preprocess((val) => val ?? 0, z.number()),
  resolutionTimes: z.preprocess((val) => val ?? [], z.array(z.number())),
  csatRating: z.preprocess((val) => val ?? 0, z.number()),
});

export const newClosedTicketsDataSchema = z.object({
  CREATED_COUNT: z.preprocess((val) => val ?? 0, z.number()),
  CLOSED_ONLY_COUNT: z.preprocess((val) => val ?? 0, z.number()),
  RESOLVED_COUNT: z.preprocess((val) => val ?? 0, z.number()),
  START_DATE: z.string(),
  END_DATE: z.string(),
});

export const userAnalyticsDataSchema = z.object({
  ASSIGNEE: z.string(),
  NAME: z.string(),
  EMAIL: z.string(),
  MESSAGES_SENT: z.preprocess((val) => val ?? 0, z.number()),
  TICKETS_REPLIED_TO: z.preprocess((val) => val ?? 0, z.number()),
  CONVERSATIONS_CLOSED: z.preprocess((val) => val ?? 0, z.number()),
  RESOLVED_TICKETS: z.preprocess((val) => val ?? 0, z.number()),
  AVG_CSAT_RATING: z.preprocess((val) => val ?? 0, z.number()),
  AVG_CONVERSATION_TOUCH_COUNT: z.preprocess((val) => val ?? 0, z.number()),
  FIRST_MERCHANT_RESPONSE_LAG: z.preprocess(
    (val) => val ?? [],
    z.array(z.number()),
  ),
  RESOLUTION_LAG: z.preprocess((val) => val ?? [], z.array(z.number())),
});

export const supportAnalyticsDataSchema = z.object({
  TOTAL_TICKETS: z.number(),
  CREATED_TICKETS: z.number(),
  RESOLVED_TICKETS: z.number(),
  CLOSED_TICKETS: z.number(),
  ONE_TOUCH_RESOLVED_TICKETS: z.number(),
  TWO_TOUCH_RESOLVED_TICKETS: z.number(),
  THREE_TOUCH_RESOLVED_TICKETS: z.number(),
  MULTI_TOUCH_RESOLVED_TICKETS: z.number(),
  AVG_CONVERSATION_TOUCH_COUNT: z.number(),
  MEDIAN_RESOLUTION_LAG: z.number(),
  MEDIAN_FIRST_MERCHANT_RESPONSE_LAG: z.number(),
  AVG_RESPONSE_TIME: z.number(),
  AVG_CSAT_RATING: z.number(),
  PLATFORM_COUNTS: z.array(z.record(z.string(), z.number())),
  TAG_COUNTS: z.array(
    z.object({ name: z.string(), count: z.number(), pillTheme: z.string() }),
  ),
  CSAT_SURVEYS_SENT: z.number(),
  CSAT_RESPONSE_COUNT: z.number(),
});

export const timeSeriesPointSchema = z.object({
  date: z.date(),
  count: z.number(),
});

export const aiMetricsSchema = z.object({
  ticketAutomationRate: z.number(),
  totalTicketsAutomated: z.number(),
  medianResolutionTime: z.number(),
  averageCsatScore: z.number(),
  costSavingsAnalysis: z.object({
    assumptions: z.object({
      annualSalary: z.number(),
      weeksPerYear: z.number(),
      ticketsPerDay: z.number(),
      workDaysPerWeek: z.number(),
      source: z.string(),
    }),
    savings: z.number(),
  }),
});

export const humanMetricsSchema = z.object({
  medianResolutionTime: z.number(),
  averageCsatScore: z.number(),
});

export const timeSeriesSchema = z.object({
  numberOfTicketsResolved: z.array(timeSeriesPointSchema),
});

export const conversationSegmentSchema = z.object({
  label: z.string(),
  newCount: z.preprocess((val) => val ?? 0, z.number()),
  closedCount: z.preprocess((val) => val ?? 0, z.number()),
  resolvedCount: z.preprocess((val) => val ?? 0, z.number()),
});

export const conversationChannelSchema = z.object({
  name: z.string(),
  count: z.preprocess((val) => val ?? 0, z.number()),
});

export const userSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  messagesSent: z.preprocess((val) => val ?? 0, z.number()),
  conversationsRepliedTo: z.preprocess((val) => val ?? 0, z.number()),
  conversationsClosed: z.preprocess((val) => val ?? 0, z.number()),
  conversationsResolved: z.preprocess((val) => val ?? 0, z.number()),
  medianFirstResponseTime: z.preprocess((val) => val ?? 0, z.number()),
  medianResolutionTime: z.preprocess((val) => val ?? 0, z.number()),
  csatRating: z.number().nullable(),
  avgConversationTouchCount: z.preprocess((val) => val ?? 0, z.number()),
});

export const tagSchema = z.object({
  name: z.string(),
  count: z.preprocess((val) => val ?? 0, z.number()),
  pillTheme: z.string(),
  percentChange: z.preprocess((val) => val ?? 0, percentChangeSchema),
});

export const multiTouchTimeSeriesSchema = z.object({
  date: z.date(),
  oneTouchCount: z.preprocess((val) => val ?? 0, z.number()),
  twoTouchCount: z.preprocess((val) => val ?? 0, z.number()),
  threeTouchCount: z.preprocess((val) => val ?? 0, z.number()),
  multiTouchCount: z.preprocess((val) => val ?? 0, z.number()),
});

export const averageResponseTimeTimeSeriesSchema = z.object({
  date: z.date(),
  responseTime: z.preprocess((val) => val ?? 0, z.number()),
});

export const messageTopicSchema = z.object({
  topic: z.string(),
  count: z.preprocess((val) => val ?? 0, z.number()),
});

export const deflectedTicketSchema = z.object({
  month: z.string(),
  count: z.preprocess((val) => val ?? 0, z.number()),
});

// AI Analytics schemas (from concierge analytics)
export const aiAnalyticsCategorySchema = z.object({
  topic: z.string(),
  count: z.number(),
});

export const aiAnalyticsSessionTimeSeriesSchema = z.object({
  date: z.string(),
  sessionsCheckoutCompleted: z.number(),
  sessionsWithConciergeAttributedCheckoutCompleted: z.number(),
  sessionsWithConciergeInteracted: z.number(),
  sessionsWithConciergePresent: z.number(),
});

export const aiAnalyticsSalesTimeSeriesSchema = z.object({
  date: z.string(),
  totalSales: z.number(),
});

export const aiAnalyticsSchema = z.object({
  // Sales metrics
  totalSales: z.number(),
  totalSalesPercentChange: percentChangeSchema,
  totalOrders: z.number(),
  totalOrdersPercentChange: percentChangeSchema,
  averageOrderValue: z.number(),
  averageOrderValuePercentChange: percentChangeSchema,
  nonEngagedAverageOrderValue: z.number(),
  nonEngagedAverageOrderValuePercentChange: percentChangeSchema,

  // Session metrics
  totalSessionsCheckoutCompleted: z.number(),
  totalSessionsWithConciergeAttributedCheckoutCompleted: z.number(),
  totalSessionsWithConciergeInteracted: z.number(),
  totalSessionsWithConciergePresent: z.number(),

  // Derived metrics
  engagedSessionConversionRate: z.number(),
  engagedSessionConversionRatePercentChange: percentChangeSchema,
  sessionConversionRate: z.number(),
  sessionConversionRatePercentChange: percentChangeSchema,
  ordersGenerated: z.number(),
  engagementRate: z.number(),
  engagementRatePercentChange: percentChangeSchema,

  // Other metrics
  deflectedTickets: z.number(),
  deflectedTicketsPercentChange: percentChangeSchema,
  productRecommendations: z.number(),
  productRecommendationsPercentChange: percentChangeSchema,

  // Time series and categorical data
  categories: z.array(aiAnalyticsCategorySchema),
  sessionTimeSeries: z.array(aiAnalyticsSessionTimeSeriesSchema),
  salesTimeSeries: z.array(aiAnalyticsSalesTimeSeriesSchema),
  currency: z.string(),
});

// Base support analytics schema (everything above AI-specific metrics)
export const baseSupportAnalyticsOutputSchema = z.object({
  // General support metrics
  ticketVolume: z.number(),
  ticketVolumePercentChange: percentChangeSchema,
  createdTickets: z.number(),
  createdTicketsPercentChange: percentChangeSchema,
  resolvedTickets: z.number(),
  resolvedTicketsPercentChange: percentChangeSchema,
  closedTickets: z.number(),
  closedTicketsPercentChange: percentChangeSchema,
  oneTouchResolvedTickets: z.number(),
  twoTouchResolvedTickets: z.number(),
  threeTouchResolvedTickets: z.number(),
  multiTouchResolvedTickets: z.number(),
  avgConversationTouchCount: z.number(),
  medianFirstResponseTime: z.number(),
  medianFirstResponseTimePercentChange: percentChangeSchema,
  medianResolutionTime: z.number(),
  medianResolutionTimePercentChange: percentChangeSchema,
  csatRating: z.number(),
  csatRatingPercentChange: percentChangeSchema.optional(),
  csatSurveysSentPercentChange: percentChangeSchema,
  csatSurveysSent: z.number(),
  csatResponseRate: z.number(),
  csatResponseRatePercentChange: percentChangeSchema.optional(),

  // Time series and breakdown data
  conversationsBySegment: z.array(conversationSegmentSchema),
  conversationsByChannel: z.array(conversationChannelSchema),
  users: z.array(userSchema),
  tags: z.array(tagSchema),
  multiTouchTicketsTimeSeries: z.array(multiTouchTimeSeriesSchema),
  averageResponseTimeTimeSeries: z.array(averageResponseTimeTimeSeriesSchema),
  messageTopics: z.array(messageTopicSchema),
});

// AI analytics schema (everything from AI-specific metrics down)
export const aiSupportAnalyticsOutputSchema = z.object({
  // AI-specific metrics (current and previous periods)
  current: z.object({
    aiMetrics: aiMetricsSchema,
    humanMetrics: humanMetricsSchema,
    aiTimeSeries: timeSeriesSchema,
    humanTimeSeries: timeSeriesSchema,
  }),
  previous: z.object({
    aiMetrics: aiMetricsSchema,
    humanMetrics: humanMetricsSchema,
    aiTimeSeries: timeSeriesSchema,
    humanTimeSeries: timeSeriesSchema,
  }),

  // Static AI metrics
  deflectedTicketsByMonth: z.array(deflectedTicketSchema),

  // AI Analytics (concierge/commerce focused metrics)
  aiAnalytics: aiAnalyticsSchema.optional(),
});

// Parameters schema for base support analytics
export const baseSupportAnalyticsParamsSchema = z.object({
  teamId: z.string(),
  platforms: z.array(createEnum(ConversationPlatform)).optional(),
  startFormatted: z.string(),
  endFormatted: z.string(),
  prevStartFormatted: z.string(),
  prevEndFormatted: z.string(),
  team: z.any(), // Team model - using z.any() for now as it's a Mongoose model
});

// Parameters schema for AI support analytics
export const aiSupportAnalyticsParamsSchema = z.object({
  teamId: z.string(),
  platforms: z.array(createEnum(ConversationPlatform)).optional(),
  startFormatted: z.string(),
  endFormatted: z.string(),
  prevStartFormatted: z.string(),
  prevEndFormatted: z.string(),
  conciergeEnabled: z.boolean(),
  attributionWindow: z.number().optional(),
});

export const supportAnalyticsInputSchema = z.object({
  dateRange: z.object({ start: z.date(), end: z.date() }),
  platforms: z.array(createEnum(ConversationPlatform)).optional(),
  attributionWindow: z.number().optional(),
});

export const supportAnalyticsOutputSchema =
  baseSupportAnalyticsOutputSchema.merge(aiSupportAnalyticsOutputSchema);

// Export inferred types for use in implementations
export type SupportAnalyticsInput = z.infer<typeof supportAnalyticsInputSchema>;
export type SupportAnalyticsOutput = z.infer<
  typeof supportAnalyticsOutputSchema
>;
export type BaseSupportAnalyticsOutput = z.infer<
  typeof baseSupportAnalyticsOutputSchema
>;
export type AISupportAnalyticsOutput = z.infer<
  typeof aiSupportAnalyticsOutputSchema
>;
export type BaseSupportAnalyticsParams = z.infer<
  typeof baseSupportAnalyticsParamsSchema
>;
export type AISupportAnalyticsParams = z.infer<
  typeof aiSupportAnalyticsParamsSchema
>;

// Internal parameter types
export type SupportAnalyticsParams = z.infer<
  typeof supportAnalyticsParamsSchema
>;
export type AnalyticsParams = z.infer<typeof analyticsParamsSchema>;

// Raw database response types
export type SupportAnalyticsRawData = z.infer<
  typeof supportAnalyticsRawDataSchema
>;
export type AIUserAnalytics = z.infer<typeof aiUserAnalyticsSchema>;
export type HumanUserAnalytics = z.infer<typeof humanUserAnalyticsSchema>;
export type NewClosedTicketsData = z.infer<typeof newClosedTicketsDataSchema>;
export type UserAnalyticsData = z.infer<typeof userAnalyticsDataSchema>;
export type SupportAnalyticsData = z.infer<typeof supportAnalyticsDataSchema>;

// Output data types
export type TimeSeriesPoint = z.infer<typeof timeSeriesPointSchema>;
export type AIMetrics = z.infer<typeof aiMetricsSchema>;
export type HumanMetrics = z.infer<typeof humanMetricsSchema>;
export type ConversationSegment = z.infer<typeof conversationSegmentSchema>;
export type ConversationChannel = z.infer<typeof conversationChannelSchema>;
export type User = z.infer<typeof userSchema>;
export type Tag = z.infer<typeof tagSchema>;
export type MultiTouchTimeSeries = z.infer<typeof multiTouchTimeSeriesSchema>;
export type AverageResponseTimeTimeSeries = z.infer<
  typeof averageResponseTimeTimeSeriesSchema
>;
export type MessageTopic = z.infer<typeof messageTopicSchema>;
export type DeflectedTicket = z.infer<typeof deflectedTicketSchema>;
export type AIAnalyticsCategory = z.infer<typeof aiAnalyticsCategorySchema>;
export type AIAnalyticsSessionTimeSeries = z.infer<
  typeof aiAnalyticsSessionTimeSeriesSchema
>;
export type AIAnalyticsSalesTimeSeries = z.infer<
  typeof aiAnalyticsSalesTimeSeriesSchema
>;
export type AIAnalytics = z.infer<typeof aiAnalyticsSchema>;
